/**
 * Open issues by severity and by kind.
 *
 * Severity alone says how bad things are but not where the work is: two
 * metadata problems and two content problems are the same bar and completely
 * different afternoons. The category list carries that, and every row links to
 * its own filtered view so the number is a starting point rather than a fact
 * to admire.
 */

import { Link } from 'react-router-dom';

import { CheckCircleIcon } from '@/components/icons';
import { cn, severityBarClass } from '@/utils/format';
import type { IssueSeverity } from '@/types/api';

const SEVERITY_ORDER: IssueSeverity[] = ['critical', 'error', 'warning', 'opportunity', 'info'];
const MAX_CATEGORIES = 6;

export function IssueBreakdown({
  distribution,
  categories,
  siteId,
}: {
  distribution: Record<string, number>;
  categories: Record<string, number>;
  siteId: string;
}) {
  const bars = SEVERITY_ORDER.map((severity) => ({
    severity,
    count: distribution[severity] ?? 0,
  })).filter((row) => row.count > 0);
  const total = bars.reduce((sum, row) => sum + row.count, 0);

  if (total === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <CheckCircleIcon className="h-4 w-4 text-success" />
        No open issues recorded.
      </p>
    );
  }

  const topCategories = Object.entries(categories)
    .sort(([, a], [, b]) => b - a)
    .slice(0, MAX_CATEGORIES);
  const categoryMax = topCategories.length > 0 ? topCategories[0][1] : 1;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex h-2 overflow-hidden rounded-full bg-surface-sunken">
          {bars.map(({ severity, count }) => (
            <span
              key={severity}
              className={severityBarClass[severity]}
              style={{ width: `${(count / total) * 100}%` }}
              title={`${count} ${severity}`}
            />
          ))}
        </div>
        <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
          {bars.map(({ severity, count }) => (
            <li key={severity}>
              <Link
                to={`/sites/${siteId}/issues?severity=${severity}`}
                className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink"
              >
                <span
                  aria-hidden="true"
                  className={cn('h-2 w-2 rounded-full', severityBarClass[severity])}
                />
                {severity}
                <span className="tnum font-semibold text-ink">{count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {topCategories.length > 0 ? (
        <div>
          <p className="mb-2 text-[11px] font-semibold tracking-wider text-ink-subtle uppercase">
            Where the work is
          </p>
          <ul className="space-y-1.5">
            {topCategories.map(([category, count]) => (
              <li key={category}>
                <Link
                  to={`/sites/${siteId}/issues?category=${category}`}
                  className="group flex items-center gap-2.5 text-xs"
                >
                  <span className="w-[6.5rem] shrink-0 truncate text-ink-muted group-hover:text-ink">
                    {category.replace(/_/g, ' ')}
                  </span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                    <span
                      className="block h-full rounded-full bg-brand/70"
                      style={{ width: `${Math.max(8, (count / categoryMax) * 100)}%` }}
                    />
                  </span>
                  <span className="tnum w-4 shrink-0 text-right font-semibold text-ink">
                    {count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
